import { FC, useEffect, useState } from "react";
import { StockMovement } from "../../../type/entities/stock-movement-type";
import * as yup from 'yup';
import { StockMovementService } from "../../../services/stockMovements/stockMovementService";
import { useAtom } from "jotai";
import { formsSubmitAtom } from "../../../atom/atom";
import useHandleErrors from "../../../hooks/useHandleErrors";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import useProviders from "../../../hooks/api-calls/useProviders";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { useFormik } from "formik";
import { toast } from "sonner";
import CustomSelect from "../../../components/forms/CustomSelect";
import useProducts from "../../../hooks/api-calls/useProducts";
import RequiredInput from "../../../components/forms/InputHOC";
import { Textarea } from "rizzui";
import { STOCK_MOVEMENT_TYPE } from "../../../utils/mapping";

interface StockMovementsFormProps {
    setOpenModal: any;
    refetch: () => void;
    stockMovement?: StockMovement | null;
    formRef?: any;
}

const schema = yup.object({
    type: yup.string().required('Tipo es requerido'),
    productId: yup.string().required('Producto es requerido'),
    quantity: yup.number().required('Cantidad es requerida').min(1, 'Cantidad debe ser mayor a 0'),
    totalPrice: yup.number().required('Precio total es requerido').min(1, 'Precio total debe ser mayor a 0'),
    source: yup.string().required('Origen es requerido'),
    providerId: yup.string().nullable(),
    description: yup.string().nullable(),
})


const superAdminSchema = schema.concat(yup.object({
    company: yup.string().required('La compañía es obligatoria'),
}))


const getOptionsType = () => {
    return [
        { label: 'Entrada', value: 'input' },
        { label: 'Salida', value: 'output' },
    ]
}


const StockMovementsForm: FC<StockMovementsFormProps> = ({
    setOpenModal,
    refetch,
    stockMovement,
    formRef,
}) => {

    // STATES

    const service = new StockMovementService();
    const isEdit = stockMovement !== null;
    const [isSearched, setIsSearched] = useState<boolean>(false);

    // HOOKS

    const [, setIsSubmitting] = useAtom(formsSubmitAtom);
    const { handleErrors } = useHandleErrors();
    const { getCompaniesList } = useCompanies();
    const { getProvidersList } = useProviders();
    const { getProductsList, getProductPrice } = useProducts();
    const { userCan } = usePrivilege();

    // FORMIK

    const formik = useFormik({
        initialValues: {
            id: stockMovement?.id ?? '',
            type: stockMovement?.type ?? '',
            productId: stockMovement?.product.id ?? '',
            quantity: stockMovement?.quantity ?? 0,
            totalPrice: stockMovement?.totalPrice ?? 0,
            source: stockMovement?.source ?? '',
            providerId: stockMovement?.provider?.id ?? '',
            description: stockMovement?.description ?? '',
            company: stockMovement?.company?.id ?? '',
            userId: stockMovement?.createdBy?.id ?? '',
        },
        validationSchema: userCan('list', 'company') ? superAdminSchema : schema,
        validateOnBlur: false,
        onSubmit: values => {
            if (!isEdit) {
                handleCreate(values);
            } else {
                handleEdit(values);
            }
        }
    });

    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR UN MOVIMIENTO DE STOCK
     * @EN CREATE A STOCK MOVEMENT
     * 
     * @param values
     */
    //-----------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            const response = await (await service.createStockMovement(values)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Movimiento de stock creado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    }
    //-----------------------------------------------------------------------------------------------------------------

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UN MOVIMIENTO DE STOCK
     * @EN EDIT A STOCK MOVEMENT
     * 
     * @param values 
     */
    //-----------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {
            const response = await (await service.editStockMovement(values)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Movimiento de stock editado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    }
    //-----------------------------------------------------------------------------------------------------------------


    // USE EFFECTS

    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting]);

    // Estado para controlar si el precio ha sido modificado manualmente
    const [manualPriceEdit, setManualPriceEdit] = useState(false);

    useEffect(() => {
        // Solo calcular automáticamente si no ha sido editado manualmente
        if (!manualPriceEdit && formik.values.productId && formik.values.quantity > 0) {
            const productPrice = getProductPrice(formik.values.productId);
            const calculatedTotal = productPrice * formik.values.quantity;
            
            // Solo actualizar si el valor calculado es diferente al actual
            if (calculatedTotal !== formik.values.totalPrice) {
                formik.setFieldValue('totalPrice', calculatedTotal);
            }
        }
    }, [formik.values.productId, formik.values.quantity, getProductPrice, manualPriceEdit]);

    // Resetear el flag cuando cambie el producto o la cantidad
    useEffect(() => {
        setManualPriceEdit(false);
    }, [formik.values.productId, formik.values.quantity]);

    useEffect(() => {
        if (!formik.values.company && getCompaniesList().length > 0 && !isSearched) {
            setIsSearched(true);
            let defaultCompany = getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU');
            formik.setFieldValue('company', defaultCompany?.value ?? '');
        }
    }, [getCompaniesList]);

    // RENDER

    return (
        <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef ? formRef : undefined}>
                <div className={'md:col-span-12'}>
                    <div className="md:grid md:grid-cols-12 gap-4">
                        
                        {userCan('list', 'company') && (
                            <CustomSelect
                                required
                                isSearchable
                                id={'company'}
                                label="Compañia"
                                containerClassName='md:col-span-12'
                                value={
                                    formik.values.company ?
                                    { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                    getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU')
                                }
                                options={getCompaniesList()}
                                onChange={
                                    (e: any) => { formik.setFieldValue('company', e?.value) }
                                }
                                error={formik.errors.company}
                                display={userCan('list', 'company')}
                            />
                        )}

                        <CustomSelect
                            label="Tipo"
                            id="type"
                            required
                            options={
                                STOCK_MOVEMENT_TYPE
                            }
                            containerClassName="md:col-span-12"
                            value={
                                STOCK_MOVEMENT_TYPE?.find((g: any) => g.value === formik.values.type)
                            }
                            onChange={
                                (e: any) => { formik.setFieldValue('type', e?.value) }
                            }
                            error={formik.touched.type && formik.errors.type}
                        />

                        <CustomSelect
                            label="Producto"
                            id="productId"
                            required
                            isSearchable
                            containerClassName='md:col-span-12'
                            value={{ value: formik.values.productId, label: getProductsList()?.find((g: any) => g.value === formik.values.productId)?.label }}
                            options={getProductsList()}
                            onChange={
                                (e: any) => { formik.setFieldValue('productId', e?.value) }
                            }
                            error={formik.touched.productId && formik.errors.productId}
                        />

                        <CustomSelect
                            label="Proveedor"
                            id="providerId"
                            isSearchable
                            containerClassName='md:col-span-12'
                            value={{ value: formik.values.providerId, label: getProvidersList()?.find((g: any) => g.value === formik.values.providerId)?.label }}
                            options={getProvidersList()}
                            onChange={
                                (e: any) => { formik.setFieldValue('providerId', e?.value) }
                            }
                            error={formik.touched.providerId && formik.errors.providerId}
                            display={formik.values.type === 'input'}
                        />

                        <RequiredInput
                            label="Cantidad"
                            id="quantity"
                            type="number"
                            className='md:col-span-12'
                            value={formik.values.quantity}
                            onChange={(e: any) => { formik.setFieldValue('quantity', e.target.value) }}
                            error={formik.touched.quantity && formik.errors.quantity}
                        />

                        <RequiredInput
                            label="Precio Total del movimiento"
                            id="totalPrice"
                            type="number"
                            className="md:col-span-12"
                            value={formik.values.totalPrice}
                            onChange={(e: any) => { 
                                formik.setFieldValue('totalPrice', e.target.value);
                                setManualPriceEdit(true);
                            }}
                            error={formik.touched.totalPrice && formik.errors.totalPrice}
                        />

                        <Textarea
                            label="origen/destino"
                            id="source"
                            rows={2}
                            value={formik.values.source}
                            onChange={(e: any) => { formik.setFieldValue('source', e.target.value) }}
                            className="mb-4 md:col-span-12"
                            required
                            error={ formik.errors.source }
                        />
                        
                        <Textarea
                            label="Descripción"
                            id="description"
                            rows={4}
                            value={formik.values.description}
                            onChange={(e: any) => { formik.setFieldValue('description', e.target.value) }}
                            className="mb-4 md:col-span-12"
                        />
                        



                    </div>
                </div>
            </form>
        </div>
    )
}

export default StockMovementsForm;





